import { useEffect, useId, useRef, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  ClickAwayListener,
  IconButton,
  InputBase,
  Tooltip,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { buildCarrierProfileUrl } from "components/CompanyProfile/utils";
import {
  BG_ICON_COLOR,
  CarrierProfileSource,
  ClientAppEvent,
  HEADER_FIELD_BG,
  HEADER_FIELD_BG_HOVER,
  HEADER_FIELD_MUTED_WHITE,
} from "constants/index";
import { useRoleContext } from "hooks";

import { CarrierSearchResultsDropdown } from "./components/CarrierSearchResultsDropdown";
import { CARRIER_SEARCH_INPUT_WIDTH } from "./constants";
import { useCarrierCompanySearch } from "./hooks/useCarrierCompanySearch";
import { buildCarrierSearchOptionId } from "./utils";

import type { CarrierSearchResult } from "./types";

const NO_HIGHLIGHT_INDEX = -1;

export function CarrierSearchBar() {
  const postHog = usePostHog();
  const { role, company } = useRoleContext();

  const [searchValue, setSearchValue] = useState<string>("");
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState<boolean>(false);
  const [highlightedIndex, setHighlightedIndex] =
    useState<number>(NO_HIGHLIGHT_INDEX);

  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  /*
  Mirrors the HasShipperRoleInCompany permission the search endpoint enforces:
  a shipper role that belongs to a company. Anyone else would only ever get a
  403 back, so they are not shown the field at all.
  */
  const canSearchCarriers = role?.type === "SHIPPER" && company != null;

  const search = useCarrierCompanySearch({
    searchValue,
    enabled: canSearchCarriers && isDropdownOpen,
  });
  const { results } = search;

  // Focus has to wait for the field to actually be rendered by the toggle.
  useEffect(() => {
    if (isMobileSearchOpen) {
      inputRef.current?.focus();
    }
  }, [isMobileSearchOpen]);

  if (!canSearchCarriers) {
    return null;
  }

  const isSearchOpen = isDropdownOpen || isMobileSearchOpen;
  const shouldShowDropdown = isDropdownOpen && searchValue.trim().length > 0;
  const mobileToggleLabel = isMobileSearchOpen
    ? "Close carrier search"
    : "Search carriers";
  /*
  Names the highlighted row so a screen reader announces it as the arrow keys
  move: the rows never take focus, so nothing else reports the move.
  */
  const highlightedOptionId =
    shouldShowDropdown && highlightedIndex !== NO_HIGHLIGHT_INDEX
      ? buildCarrierSearchOptionId(listboxId, highlightedIndex)
      : undefined;

  const closeSearch = () => {
    setIsDropdownOpen(false);
    setIsMobileSearchOpen(false);
    // Reopening the field starts a fresh pass over the results, so a highlight
    // held from the closed one would be reported by `aria-activedescendant`.
    setHighlightedIndex(NO_HIGHLIGHT_INDEX);
  };

  const handleSearchValueChange = (value: string) => {
    setSearchValue(value);
    setIsDropdownOpen(true);
    // A changed query returns a different result set, so a highlight held over
    // from the previous one would point at an unrelated carrier.
    setHighlightedIndex(NO_HIGHLIGHT_INDEX);
  };

  /*
  The profile opens in its own tab, so the shipper is left on the page they
  searched from with what they typed still in the field, ready to open a second
  carrier without retyping it. The row is an anchor and navigates on its own;
  this only records the pick and closes the dropdown.
  */
  const handleSelectResult = (result: CarrierSearchResult) => {
    postHog?.capture(ClientAppEvent.CARRIER_SEARCH_RESULT_SELECTED, {
      selected_company_id: result.company.id,
      search_query_length: searchValue.trim().length,
      result_count: results.length,
    });

    closeSearch();
  };

  // The enter key never reaches the row's anchor, so it opens the tab itself.
  // Called from the keydown handler, which is the user gesture a popup blocker
  // looks for.
  const openHighlightedResult = (result: CarrierSearchResult) => {
    handleSelectResult(result);
    window.open(
      buildCarrierProfileUrl(
        result.company.id,
        CarrierProfileSource.HEADER_SEARCH,
      ),
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      closeSearch();
      inputRef.current?.blur();
      return;
    }

    if (!shouldShowDropdown || results.length === 0) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((previous) =>
        previous < results.length - 1 ? previous + 1 : previous,
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((previous) =>
        previous > 0 ? previous - 1 : NO_HIGHLIGHT_INDEX,
      );
    } else if (event.key === "Enter") {
      event.preventDefault();
      const highlightedResult = results[highlightedIndex];
      if (highlightedResult) {
        openHighlightedResult(highlightedResult);
      }
    }
  };

  const handleSearchFocus = () => {
    setIsDropdownOpen(true);
    postHog?.capture(ClientAppEvent.CARRIER_SEARCH_BAR_CLICKED);
  };

  return (
    /*
    The header never unmounts, so the document listeners are armed only while
    something is open rather than running on every click in the app.
    */
    <ClickAwayListener
      onClickAway={closeSearch}
      mouseEvent={isSearchOpen ? "onClick" : false}
      touchEvent={isSearchOpen ? "onTouchEnd" : false}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Tooltip title={mobileToggleLabel}>
          <IconButton
            aria-label={mobileToggleLabel}
            aria-expanded={isMobileSearchOpen}
            onClick={() => setIsMobileSearchOpen((previous) => !previous)}
            sx={{ display: { xs: "flex", sm: "none" }, color: "white" }}
          >
            {isMobileSearchOpen ? <CloseIcon /> : <SearchIcon />}
          </IconButton>
        </Tooltip>

        {/*
        On a phone the field has no room beside the header actions, so the icon
        above toggles it into a full-width strip below the bar. From `sm` up it
        is always shown inline.
        */}
        <Box
          sx={{
            position: { xs: "absolute", sm: "relative" },
            top: { xs: "100%", sm: "auto" },
            left: { xs: 0, sm: "auto" },
            right: { xs: 0, sm: "auto" },
            width: { xs: "100%", sm: "fit-content" },
            maxWidth: "100%",
            mr: { xs: 0, sm: 1 },
            display: {
              xs: isMobileSearchOpen ? "block" : "none",
              sm: "block",
            },
            bgcolor: { xs: BG_ICON_COLOR, sm: "transparent" },
            p: { xs: 2, sm: 0 },
            zIndex: (theme) => theme.zIndex.appBar,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              bgcolor: HEADER_FIELD_BG,
              "&:hover": { bgcolor: HEADER_FIELD_BG_HOVER },
              borderRadius: 1,
              px: 1.5,
              transition: "background-color 0.2s",
            }}
          >
            <SearchIcon
              sx={{ color: HEADER_FIELD_MUTED_WHITE, mr: 1, fontSize: 20 }}
            />
            <InputBase
              inputRef={inputRef}
              placeholder="Search carriers"
              value={searchValue}
              onChange={(event) => handleSearchValueChange(event.target.value)}
              onFocus={handleSearchFocus}
              onKeyDown={handleKeyDown}
              inputProps={{
                "aria-label": "Search carriers",
                "aria-controls": shouldShowDropdown ? listboxId : undefined,
                "aria-expanded": shouldShowDropdown,
                "aria-autocomplete": "list",
                "aria-activedescendant": highlightedOptionId,
                role: "combobox",
              }}
              sx={{
                color: "white",
                width: "100%",
                fontSize: "0.875rem",
                "& .MuiInputBase-input": {
                  py: 0.75,
                  width: { xs: "100%", sm: CARRIER_SEARCH_INPUT_WIDTH },
                  maxWidth: "100%",
                  "&::placeholder": {
                    color: HEADER_FIELD_MUTED_WHITE,
                    opacity: 1,
                  },
                },
              }}
            />
          </Box>

          {shouldShowDropdown && (
            <CarrierSearchResultsDropdown
              searchValue={searchValue.trim()}
              search={search}
              highlightedIndex={highlightedIndex}
              listboxId={listboxId}
              onSelect={handleSelectResult}
            />
          )}
        </Box>
      </Box>
    </ClickAwayListener>
  );
}
